import React from 'react'
import { useStateMachine } from 'little-state-machine'
import { useForm, FormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMutation, queryCache } from 'react-query'

import api from '../../utils/api'
import { Store } from './Scale'

import { InquiryFormType } from '../../utils/constants'
import { isEmptyObject } from '../../utils/isEmptyObject'

import { useNotifications } from '../../containers/Notifications'
import { useWithAuth } from '../../containers/App'

import {
  resetAction,
  setFormInitialValues,
  updateAction,
  backwardsAction,
} from './actions'
import { FormValues, InquiryModel } from './types'
import { Button, Footer, Section, Form } from './Layout'
import { Progress } from './Progress'
import { SuccessScreen } from './InfoScreens/SuccessScreen'
import { ID } from '../../types/id'
import { Field } from './Fields/Field'
import { SpeLevel } from '../../utils/constants'
import { encodeLanguage, filterScalesByLanguage } from './utils'

const postInquiryAnswers = '/form/rest/inquiry/response'

type Props = {
  formType: InquiryFormType['name']
  scaleId: ID
  code: string
  speLevel: SpeLevel
  isDiagnostician: boolean
  isEdition: boolean
  examinationId: ID | null
  initialData?: InquiryModel
}
export function Question({
  formType,
  code,
  speLevel,
  scaleId,
  isDiagnostician,
  isEdition,
  examinationId,
  initialData,
}: Props) {
  const withAuth = useWithAuth()
  const [sent, setSend] = React.useState(false)
  const { notify } = useNotifications()
  const { t, i18n } = useTranslation('translation')
  const language = i18n.language ?? 'pl'
  const currentScale = filterScalesByLanguage(language, formType, speLevel)
  const submitFunction = React.useCallback(
    (normalizedValues) =>
      isDiagnostician
        ? withAuth(postInquiryAnswers, {
            method: isEdition ? 'PUT' : 'POST',
            payload: {
              id: scaleId,
              code,
              answers: normalizedValues,
              lang: encodeLanguage(language),
            },
          })
        : api(postInquiryAnswers, {
            method: 'POST',
            payload: {
              id: scaleId,
              code,
              answers: normalizedValues,
              lang: encodeLanguage(language),
            },
          }),
    [isDiagnostician]
  )
  const [sendAnswers] = useMutation<InquiryModel, FormValues>((values) => {
    const normalizedValues = Object.entries(values).map(([k, v]) => {
      return {
        index: Number(k),
        answer: v,
      }
    })
    //scale.questions[index]
    return submitFunction(normalizedValues)
      .then(() => {
        setSend(true)
        examinationId && queryCache.refetchQueries(`inquiry-${examinationId}`)
      })
      .catch(() => {
        notify(t('sendAnswersFailure'), 'error')
      })
  })

  const {
    state: { inquery },
    actions,
  } = useStateMachine<Store>({
    update: updateAction,
    goBack: backwardsAction,
    reset: resetAction,
    setFormInitialValues,
  })
  const form = useForm<FormValues>()
  const { errors } = form
  const activeQuestion = React.useMemo(
    () => currentScale[inquery.activeIndex],
    [inquery.activeIndex, scaleId]
  )

  const allMatrixQuestionsByScaleType = React.useMemo(
    () =>
      // @ts-ignore
      currentScale.flatMap((scale) =>
        scale.type === 'matrix' ? scale.questions : []
      ),
    [inquery.activeIndex, scaleId]
  )
  React.useEffect(() => {
    const hasSomeEmptyFields = !isEmptyObject(errors)
    if (hasSomeEmptyFields) {
      notify(t('hasEmptyFields'), 'error')
    }
  }, [errors])

  React.useEffect(() => {
    actions.reset({})
    i18n.changeLanguage('pl')
  }, [scaleId])

  const isLastQuestion = React.useMemo(
    () => inquery.activeIndex + 1 === currentScale.length,
    [inquery.activeIndex]
  )
  const isFirstQuestion = React.useMemo(() => inquery.activeIndex === 0, [
    inquery.activeIndex,
  ])

  const onChange = () => {
    actions.goBack({})
  }
  const onSubmit = form.handleSubmit((data) => {
    if (isLastQuestion) {
      sendAnswers({
        ...inquery.answers,
        ...data,
        0: new Date().toISOString(),
      }).then((res) => res)
    } else {
      actions.update(data)
      actions.setFormInitialValues(data)
    }
  })
  React.useEffect(() => {
    const initialFormValues =
      initialData?.answers.reduce((acc, cur) => {
        return { ...acc, [cur.index]: cur.answer }
      }, {}) ?? {}
    actions.setFormInitialValues(initialFormValues)
  }, [initialData])

  if (sent) return <SuccessScreen />

  return (
    <FormContext {...form}>
      <Section>
        <Progress allQuestionsLength={currentScale.length} />
        <Form onSubmit={onSubmit}>
          <Field
            activeQuestion={activeQuestion}
            formType={formType}
            errors={form.errors}
            allMatrixQuestionsByScaleType={allMatrixQuestionsByScaleType}
          />
          <Footer>
            {!isFirstQuestion && (
              <Button tabIndex="1" type="button" onClick={onChange}>
                {t('inquiryProgress.options.back')}
              </Button>
            )}
            <Button tabIndex="1" type="submit">
              {!isLastQuestion
                ? t('inquiryProgress.options.continue')
                : t('inquiryProgress.options.send')}
            </Button>
          </Footer>
        </Form>
      </Section>
    </FormContext>
  )
}
